import React, {useEffect, useMemo, useState} from 'react';
import {Button, Icon, useRootWindow} from '@wix/devzai-utils-react';
import {classes, style} from './bymo-skip-header.st.css';
import {
    arrayRemoveUndefinedValues,
    HtmlObjectResizeMode,
    stringElipsisFromStartByLength,
    stringReplaceEmptyValue,
    stringTrimPrefix,
    urlGetHostname,
    Values
} from '@wix/devzai-utils-common';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {
    DoppePageViewerPart,
    doppePageViewerPartCreateMarkingAttributes
} from '../doppe-page-viewer-part/doppe-page-viewer-part';
import {useBymoPageContextProps} from '../../doppe-sdk';
import {
    CommonClasses,
    FlexLayout,
    WixIconChevronLeftLarge,
    WixIconChevronRightLarge,
    WixImageView
} from '@wix/devzai-common-client';
import {wixImageResourceGetResizedImageUrl} from '@wix/devzai-common-wix';
import {doppeViewerLangIsRtl} from '../../client-server-common/doppe-viewer/doppe-viewer-lang';
import {useBymoPageProps} from '../../client-server-common';
import {DomEventListener} from '@wix/devzai-utils-dom';
import {useDoppeViewerLangDictionary} from '../doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';

export const BymoSkipHeaderSkin = {
    Standard: 'standard',
    Round: 'round'
} as const;

export type BymoSkipHeaderSkin = Values<typeof BymoSkipHeaderSkin>;

export const BymoSkipHeader = React.memo(function BymoSkipHeader(props: BymoSkipHeader.Props) {

    const {
        className,
        skin = BymoSkipHeaderSkin.Standard,
        showTargetUrlImage = false
    } = props;

    const clientServices = useDoppeViewerClientServices();
    const pageViewerContext = useBymoPageViewerContext();
    const bymoPageContextProps = useBymoPageContextProps();
    const bymoPageProps = useBymoPageProps();
    const doppeViewerLangDictionary = useDoppeViewerLangDictionary();
    const lang = bymoPageProps.lang;

    const targetUrl = bymoPageContextProps.targetUrl;
    const targetUrlImage = bymoPageContextProps.targetUrlImage;
    const [countdown, setCountdown] = useState(bymoPageProps.autoSkipSeconds);
    const [isCanceled, setIsCanceled] = useState(false);
    const [timeoutInterval, setTimeoutInterval] = useState<NodeJS.Timeout>(setTimeout(() => {
    }, 0));

    const onCountdownComplete = useMemo(() => {

        return () => {
            const viewerContextData = pageViewerContext;

            if (targetUrl && !isCanceled) {
                if (viewerContextData.mode === BymoPageViewerMode.Live) {

                    clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirected', {
                        actionId: '',
                        pageId: viewerContextData.bymoPageId,
                        hypeUserId: viewerContextData.doppeUserId,
                        linkId: viewerContextData.linkId,
                        contextMetaData: JSON.stringify({
                            autoSkipTriggered: true,
                        }),
                        destinationUrl: targetUrl,
                    })

                    clientServices.bymoService.skipToTargetUrl({
                        targetUrl: targetUrl,
                        livePageData: viewerContextData
                    })
                } else if (viewerContextData.mode === BymoPageViewerMode.LiveInApp) {
                    location.href = targetUrl;
                } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
                    viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventAutoSkipPrevented', {})
                }
            }
        }
    }, [pageViewerContext, targetUrl, isCanceled]);

    const onCancel = useMemo(() => {

        return () => {
            const viewerContextData = pageViewerContext;

            if (viewerContextData.mode === BymoPageViewerMode.Live && targetUrl !== null) {

                clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirectCanceled', {
                    actionId: '',
                    pageId: viewerContextData.bymoPageId,
                    hypeUserId: viewerContextData.doppeUserId,
                    linkId: viewerContextData.linkId,
                    contextMetaData: JSON.stringify({
                        autoSkipTriggered: true,
                    }),
                    destinationUrl: targetUrl,
                })
            }
            clearInterval(timeoutInterval);
            setIsCanceled(true);
        }
    }, [pageViewerContext, targetUrl, timeoutInterval]);

    const rootWindow = useRootWindow();

    useEffect(() => {
        const isAutoSkipEnabled = bymoPageProps.autoSkipEnabled;
        const targetUrl = bymoPageContextProps.targetUrl;

        if (isAutoSkipEnabled && targetUrl && pageViewerContext.mode !== BymoPageViewerMode.Preview) {

            if (pageViewerContext.mode === BymoPageViewerMode.Live) {
                clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirectTriggered', {
                    actionId: '',
                    pageId: pageViewerContext.bymoPageId,
                    hypeUserId: pageViewerContext.doppeUserId,
                    linkId: pageViewerContext.linkId,
                    contextMetaData: JSON.stringify({
                        autoSkipTriggered: true,
                    }),
                    destinationUrl: targetUrl,
                })
            }


            const domListener = DomEventListener.one((rootWindow ?? window).document, 'click', () => {
                onCancel();
            })

            return () => {
                domListener.dispose();
                onCancel();
            }
        }

        return undefined;
    }, [bymoPageProps.autoSkipEnabled, isCanceled]);

    useEffect(() => {
        setIsCanceled(false);
        setCountdown(bymoPageProps.autoSkipSeconds);
    }, [bymoPageProps.autoSkipEnabled, bymoPageProps.autoSkipSeconds]);

    useEffect(() => {

        if (bymoPageProps.autoSkipEnabled) {
            const countdownInterval = setInterval(() => {

                setCountdown(countdown => {
                    const updatedCountdown = countdown - 1;

                    if (updatedCountdown === 0) {
                        clearInterval(countdownInterval);

                        setTimeout(() => {
                            onCountdownComplete();
                        })
                    }

                    return updatedCountdown;
                })
            }, 1000);

            setTimeoutInterval(countdownInterval);

            return () => {
                clearInterval(countdownInterval)
            }
        }

        return () => {
        };

    }, [bymoPageProps.autoSkipEnabled, bymoPageProps.autoSkipSeconds, onCountdownComplete]);

    if (targetUrl === null) {
        return null;
    }

    return (
        <div
            className={style(classes.root, {skin: skin, withImage: targetUrlImage !== null && showTargetUrlImage}, className, FlexLayout.row.alignToCenter, CommonClasses.button)}
            onClick={() => {
                if (pageViewerContext.mode === BymoPageViewerMode.Live || pageViewerContext.mode === BymoPageViewerMode.LiveInApp) {

                    if (pageViewerContext.mode === BymoPageViewerMode.Live) {
                        clientServices.viewerAnalyticsClient.logEvent('uouPageSkipClicked', {
                            pageId: pageViewerContext.bymoPageId,
                            hypeUserId: pageViewerContext.doppeUserId,
                            linkId: pageViewerContext.linkId,
                            destinationUrl: targetUrl
                        });

                        clientServices.viewerAnalyticsClient.logEvent('uouEngagementClicked', {
                            pageId: pageViewerContext.bymoPageId,
                            hypeUserId: pageViewerContext.doppeUserId,
                            linkId: pageViewerContext.linkId,
                            destinationUrl: targetUrl
                        });
                    }

                    clientServices.bymoService.skipToTargetUrl({
                        targetUrl: targetUrl,
                        livePageData: pageViewerContext
                    })
                } else if (
                    pageViewerContext.mode === BymoPageViewerMode.LivePreview
                ) {
                    pageViewerContext.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventSkipPrevented', {});
                }
            }}
            {...doppePageViewerPartCreateMarkingAttributes(DoppePageViewerPart.BymoSkipButton)}
        >
            {targetUrlImage !== null && showTargetUrlImage ? (
                <WixImageView
                    className={style(classes.linkImageView, classes.linkImage)}
                    src={wixImageResourceGetResizedImageUrl(targetUrlImage, {
                        resizeMode: HtmlObjectResizeMode.Cover,
                        targetHeight: 50,
                        targetWidth: 50
                    })}
                    resizeMode={'cover'}
                    positionHorizontal={'center'}
                    positionVertical={'center'}
                />
            ) : null}
            <div
                className={style(classes.itemContent, FlexLayout.column.default, FlexLayout.fillRemainingHorizontalSpace)}
            >
                <div
                    className={style(
                        classes.filePathLabel,
                        CommonClasses.ellipsis
                    )}
                >
                    {doppeViewerLangDictionary.beforeYouMoveOn}
                </div>
                <div
                    className={style(classes.speedDialTargetUrl, {})}
                >
                    {arrayRemoveUndefinedValues([
                        pageViewerContext.mode === BymoPageViewerMode.LivePreview ?
                            `www.example.com` : stringElipsisFromStartByLength(stringTrimPrefix(urlGetHostname(targetUrl), 'www.'), 22),
                        stringReplaceEmptyValue(bymoPageProps.autoSkipEnabled && countdown > 0 && !isCanceled ? doppeViewerLangDictionary.redirectingIn(countdown) : undefined, undefined)
                    ]).join(` | `)}
                </div>
            </div>
            <div className={style(classes.separator, {})}/>
            <Button
                className={style(classes.headerSkipButton, FlexLayout.row.alignToCenter)}
            >
                <div>{doppeViewerLangDictionary.skip}</div>
                <Icon
                    className={style(classes.skipChevron, FlexLayout.column.alignToEnd, FlexLayout.horizontallyJustifyToEnd)}
                    icon={doppeViewerLangIsRtl(lang) ? WixIconChevronLeftLarge : WixIconChevronRightLarge}
                />
            </Button>
        </div>
    );
});

export namespace BymoSkipHeader {

    export interface Props extends React.HTMLAttributes<any> {
        autoRedirectSeconds: number | null;
        skin?: BymoSkipHeaderSkin
        showTargetUrlImage?: boolean;
    }
}
